/** The token route's contract with the shell, shared by the device
 *  (client.ts) and the route so the two cannot drift. The body schema is in
 *  report-schema.ts. */

/** Reports that did not come back `recorded`. The count is stored, so it
 *  survives launches, but it is spent once per full PAGE LOAD rather than
 *  once per launch: a full navigation (native sign-in, the OAuth return, an
 *  email-confirm link) starts a new report, and nothing re-reports within a
 *  page that stays open, backgrounding included. Past this the device stops
 *  asking: the Simulator, for one, can never produce a token. */
export const MAX_REPORT_ATTEMPTS = 5;

/** Error text in a report or its event: Apple's, Postgres's or the device's,
 *  none of it ours to size. */
export const REPORT_DETAIL_MAX_LENGTH = 200;

/** An AdServices token's alphabet (base64, before any `=` padding): the
 *  route's check and the event's token mask are both built from it. */
export const TOKEN_CHARS = "A-Za-z0-9+/";

/** The route's answer. `recorded: false` means try again with a fresh token
 *  on a later launch, whatever the reason; the attempt counter caps that. */
export type RecordTokenResult = { recorded: boolean };

/** How one report ended. The route sends each to PostHog
 *  (trackAppleAdsInstallReport) and answers the device from it. */
export type ReportOutcome =
  /** Apple answered and this report wrote the row. */
  | { outcome: "recorded"; attributed: boolean; sample: boolean }
  /** A repeat for an install already recorded: a response lost to a reload,
   *  or another report for the same install that wrote the row first. */
  | { outcome: "already_recorded" }
  /** Apple rejected the token (400). */
  | { outcome: "invalid_token" }
  /** Apple had no record after all three tries (404): too soon, or a token
   *  past its 24 hours. */
  | { outcome: "not_found" }
  /** Apple 5xx, a network failure or timeout, or a body we could not read. */
  | { outcome: "unavailable"; detail: string }
  /** Apple answered but our insert failed. */
  | { outcome: "write_failed"; detail: string }
  /** A token the route already took in the last 24 hours from another
   *  install id, or from the same one past MAX_REPORT_ATTEMPTS (iOS reuses a
   *  token for a while, so an install's retries may repeat it). Another
   *  install id is a replay, or a reinstall on the same phone that day. */
  | { outcome: "token_replayed" }
  /** The device got no token from AdServices, so it never reached Apple. */
  | { outcome: "device_error"; detail: string }
  /** A body parseReportBody refused: from a real device, a client and
   *  schema out of step, which would otherwise lose every install silently. */
  | { outcome: "rejected_body"; detail: string };

export function isRecorded(outcome: ReportOutcome["outcome"]): boolean {
  return outcome === "recorded" || outcome === "already_recorded";
}
